/**
 * User Management API Service
 * Handles all User Management MicroService API calls
 */

import userManagementHttps from "../../axios/userManagement";
import {
  ValidateTokenRequest,
  ValidateTokenResponse,
  UserActivationRequest,
  UserActivationAPIResponse,
  UserDetailsAPIResponse,
  UserDetailsUpdateAPIResponse,
  UserDetailsUpdateResponse,
} from "../types/userManagement.types";
import { AxiosResponse } from "axios";

/**
 * Normalizes the update user profile response to match the expected format
 * Handles both response formats:
 * 1. Expected: { data: { usersDetails: [...] } }
 * 2. Actual: { data: { userId, firstName, ... } }
 */
function normalizeUpdateUserProfileResponse(
  response: any
): UserDetailsUpdateAPIResponse {
  // If response already has the expected format (data.usersDetails exists)
  if (response?.data?.usersDetails && Array.isArray(response.data.usersDetails)) {
    return response as UserDetailsUpdateAPIResponse;
  }

  // If response.data has user fields directly (actual API format)
  if (response?.data && response.data.userId !== undefined) {
    const userData = response.data as UserDetailsUpdateResponse;
    return {
      ...response,
      data: {
        usersDetails: [userData],
      },
    } as UserDetailsUpdateAPIResponse;
  }

  // Return as-is if format is unknown (will be handled by error handling)
  return response as UserDetailsUpdateAPIResponse;
}

/**
 * Validates a JWT token and retrieves user details
 * @param payload - Token validation request
 * @returns Promise with user details from validated token
 * @throws Error if the request fails
 */
export async function validateToken(
  payload: ValidateTokenRequest
): Promise<ValidateTokenResponse> {
  console.log("🔵 validateToken service: Making POST request", {
    endpoint: "validateToken",
    tokenPreview: payload.token.substring(0, 50) + "...",
    baseURL: `${import.meta.env.VITE_REACT_USER_MANAGEMENT_API || "http://localhost:9008"}/v1/userManagement`,
  });
  try {
    const response: AxiosResponse<ValidateTokenResponse> = await userManagementHttps.post(
      "validateToken",
      payload
    );
    console.log("🟢 validateToken service: Response received successfully", {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
    });
    return response.data;
  } catch (error: any) {
    console.error("🔴 validateToken service: Error occurred", {
      error,
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      config: {
        url: error?.config?.url,
        method: error?.config?.method,
        baseURL: error?.config?.baseURL,
      },
    });
    throw error;
  }
}

/**
 * Activates a user account
 * @param payload - User activation request
 * @returns Promise with activation response
 * @throws Error if the request fails
 */
export async function activateUser(
  payload: UserActivationRequest
): Promise<UserActivationAPIResponse> {
  const response: AxiosResponse<UserActivationAPIResponse> = await userManagementHttps.post(
    "activate",
    payload
  );
  return response.data;
}

/**
 * Gets user details by userId
 * @param userId - The user ID to fetch details for
 * @returns Promise with user details response
 * @throws Error if the request fails
 */
export async function getUserDetails(
  userId: string | number
): Promise<UserDetailsAPIResponse> {
  const baseURL = `${import.meta.env.VITE_REACT_USER_MANAGEMENT_API || "http://localhost:9008"}/v1/userManagement`;
  const endpoint = "userDetails";
  const fullURL = `${baseURL}/${endpoint}?userId=${userId.toString()}`;
  
  console.log("🔵 getUserDetails service: Making GET request", {
    endpoint,
    userId: userId.toString(),
    baseURL,
    fullURL,
    params: { userId: userId.toString() },
  });
  
  try {
    const response: AxiosResponse<UserDetailsAPIResponse> = await userManagementHttps.get(
      endpoint,
      {
        params: { userId: userId.toString() },
      }
    );
    
    console.log("🟢 getUserDetails service: Response received successfully", {
      status: response.status,
      statusText: response.statusText,
      requestURL: response.config.url,
      fullRequestURL: `${response.config.baseURL}${response.config.url}`,
      data: response.data,
    });
    return response.data;
  } catch (error: any) {
    const actualURL = error?.config?.baseURL 
      ? `${error.config.baseURL}${error.config.url || ""}`
      : "unknown";
    
    console.error("🔴 getUserDetails service: Error occurred", {
      error,
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      config: {
        url: error?.config?.url,
        method: error?.config?.method,
        baseURL: error?.config?.baseURL,
        fullURL: actualURL,
        params: error?.config?.params,
      },
    });
    throw error;
  }
}

/**
 * Gets user details by email (e.g. after verify-OTP success)
 * GET /v1/userManagement/userDetails?email=...
 * @param email - The user email to fetch details for
 * @returns Promise with user details response
 * @throws Error if the request fails
 */
export async function getUserDetailsByEmail(
  email: string
): Promise<UserDetailsAPIResponse> {
  const endpoint = "userDetails";
  try {
    const response: AxiosResponse<UserDetailsAPIResponse> =
      await userManagementHttps.get(endpoint, {
        params: { email },
      });
    console.log("🟢 getUserDetailsByEmail: Response received", {
      email,
      status: response.status,
    });
    return response.data;
  } catch (error: any) {
    console.error("🔴 getUserDetailsByEmail: Error", {
      email,
      message: error?.message,
      response: error?.response?.data,
    });
    throw error;
  }
}

/**
 * Updates user profile
 * @param payload - User profile data to update
 * @returns Promise with updated user details response
 * @throws Error if the request fails
 */
export async function updateUserProfile(
  payload: any
): Promise<UserDetailsUpdateAPIResponse> {
  const baseURL = `${import.meta.env.VITE_REACT_USER_MANAGEMENT_API || "http://localhost:9008"}/v1/userManagement`;
  const endpoint = "userProfile";
  const fullURL = `${baseURL}/${endpoint}`;
  
  console.log("🔵 updateUserProfile service: Making POST request", {
    endpoint,
    baseURL,
    fullURL,
    payload,
  });
  
  try {
    const response: AxiosResponse<UserDetailsUpdateAPIResponse> = await userManagementHttps.post(
      endpoint,
      payload
    );
    
    console.log("🟢 updateUserProfile service: Response received successfully", {
      status: response.status,
      statusText: response.statusText,
      requestURL: response.config.url,
      fullRequestURL: `${response.config.baseURL}${response.config.url}`,
      data: response.data,
    });
    
    // Normalize the response to handle both API response formats
    const normalizedResponse = normalizeUpdateUserProfileResponse(response.data);
    
    console.log("🟢 updateUserProfile service: Normalized response", {
      originalData: response.data,
      normalizedData: normalizedResponse,
    });
    
    return normalizedResponse;
  } catch (error: any) {
    const actualURL = error?.config?.baseURL 
      ? `${error.config.baseURL}${error.config.url || ""}`
      : "unknown";
    
    console.error("🔴 updateUserProfile service: Error occurred", {
      error,
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      config: {
        url: error?.config?.url,
        method: error?.config?.method,
        baseURL: error?.config?.baseURL,
        fullURL: actualURL,
      },
    });
    throw error;
  }
}

/**
 * User Management service object grouping all API calls
 */
export const userManagementService = {
  validateToken,
  activateUser,
  getUserDetails,
  updateUserProfile,
};

export default userManagementService;

